import React, { useState, useEffect } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Description as DescriptionIcon
} from '@mui/icons-material'
import { collection, query, where, getDocs, doc, updateDoc, arrayUnion } from 'firebase/firestore'
import { db } from '../firebase/config'
import { useAuth } from '../hooks/useAuth'
import toast from 'react-hot-toast'

const StudentClasses = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [joinDialogOpen, setJoinDialogOpen] = useState(false)
  const [classCode, setClassCode] = useState('')
  const [joining, setJoining] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (user && userData) {
      loadJoinedClasses()
    }
  }, [user, userData])

  const loadJoinedClasses = async () => {
    try {
      setLoading(true)
      console.log('Loading classes for user:', user.uid)
      console.log('User data:', userData)
      
      // Öğrencinin katıldığı sınıfları yükle - Android uygulamasındaki gibi
      const q = query(
        collection(db, 'classes'),
        where('studentIds', 'array-contains', user.uid)
      )
      
      console.log('Query created:', q)
      
      const querySnapshot = await getDocs(q)
      const classesData = []
      
      console.log('Found classes:', querySnapshot.size)
      console.log('Query snapshot:', querySnapshot)
      
      for (const docSnapshot of querySnapshot.docs) {
        const classData = docSnapshot.data()
        console.log('Class data:', classData)
        console.log('Class ID:', docSnapshot.id)
        console.log('Student IDs in class:', classData.studentIds)
        
        classesData.push({
          id: docSnapshot.id,
          ...classData
        })
      }
      
      setClasses(classesData)
      console.log('Final classes loaded:', classesData)
    } catch (error) {
      console.error('Error loading classes:', error)
      console.error('Error details:', error.message)
      toast.error('Sınıflar yüklenemedi: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleJoinClass = async () => {
    if (!classCode.trim()) {
      setError('Sınıf kodu boş olamaz')
      return
    }

    setJoining(true)
    setError('')

    try {
      // Sınıf koduna göre sınıfı bul
      const q = query(
        collection(db, 'classes'),
        where('joinCode', '==', classCode.trim())
      )
      
      const querySnapshot = await getDocs(q)
      
      if (querySnapshot.empty) {
        setError('Geçersiz sınıf kodu!')
        return
      }

      const classDoc = querySnapshot.docs[0]
      const classId = classDoc.id
      const classData = classDoc.data()

      // Öğrenci zaten sınıfta mı kontrol et
      if (classData.studentIds && classData.studentIds.includes(user.uid)) {
        setError('Bu sınıfa zaten katılmışsınız!')
        return
      }

      // Sınıfa öğrenciyi ekle
      await updateDoc(doc(db, 'classes', classId), {
        studentIds: arrayUnion(user.uid)
      })

      // Kullanıcı dokümanına sınıf ID'sini ekle
      await updateDoc(doc(db, 'users', user.uid), {
        classIds: arrayUnion(classId)
      })

      toast.success('Sınıfa başarıyla katıldınız!')
      setJoinDialogOpen(false)
      setClassCode('')
      loadJoinedClasses() // Sınıf listesini yenile
      
    } catch (error) {
      console.error('Error joining class:', error)
      setError('Sınıfa katılırken hata oluştu')
    } finally {
      setJoining(false)
    }
  }

  const handleClassClick = (classItem) => {
    // Sınıf detay sayfasına yönlendir
    onBack({ 
      page: 'class-detail', 
      classData: classItem 
    })
  }

  const getStudentCount = (studentIds) => {
    return studentIds ? studentIds.length : 0
  }

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Katıldığım Sınıflar
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3, pb: 10 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Classes List */}
            {classes.length === 0 ? (
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Henüz hiç sınıfa katılmamışsınız
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Sınıfa katılmak için aşağıdaki butona tıklayın
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setJoinDialogOpen(true)}
                  >
                    Sınıfa Katıl
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <List>
                {classes.map((classItem, index) => (
                  <React.Fragment key={classItem.id}>
                    <ListItem
                      button
                      onClick={() => handleClassClick(classItem)}
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        backgroundColor: 'background.paper',
                        boxShadow: 1,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          transform: 'translateY(-2px)',
                          transition: 'all 0.2s ease-in-out'
                        }
                      }}
                    >
                      <CardContent sx={{ width: '100%', py: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box flex={1}>
                            <Typography variant="h6" gutterBottom fontWeight="bold">
                              {classItem.className}
                            </Typography>
                            
                            <Box display="flex" alignItems="center" mb={1}>
                              <PersonIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                Öğretmen: {classItem.teacherName}
                              </Typography>
                            </Box>
                            
                            {classItem.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {classItem.description}
                              </Typography>
                            )}
                            
                            <Box display="flex" gap={1} flexWrap="wrap">
                              <Chip
                                icon={<GroupIcon />}
                                label={`${getStudentCount(classItem.studentIds)} öğrenci`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              {classItem.joinCode && (
                                <Chip
                                  label={`Kod: ${classItem.joinCode}`}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </ListItem>
                    {index < classes.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}

            {/* Join Class Button */}
            {classes.length > 0 && (
              <Box sx={{ position: 'fixed', bottom: 80, right: 16, zIndex: 1000 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setJoinDialogOpen(true)}
                  sx={{
                    borderRadius: '50px',
                    px: 3,
                    py: 1.5,
                    boxShadow: 3
                  }}
                >
                  Sınıfa Katıl
                </Button>
              </Box>
            )}
          </>
        )}

        {/* Join Class Dialog */}
        <Dialog open={joinDialogOpen} onClose={() => setJoinDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Sınıfa Katıl</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Katılmak istediğiniz sınıfın kodunu girin
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <TextField
              autoFocus
              margin="dense"
              label="Sınıf Kodu"
              fullWidth
              variant="outlined"
              value={classCode}
              onChange={(e) => setClassCode(e.target.value)}
              placeholder="Örn: ABC123"
              error={!!error}
              helperText={error}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setJoinDialogOpen(false)}>
              İptal
            </Button>
            <Button
              onClick={handleJoinClass}
              variant="contained"
              disabled={joining || !classCode.trim()}
            >
              {joining ? <CircularProgress size={20} /> : 'Katıl'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      </Box>
    </SpaceTheme>
  )
}

export default StudentClasses
